/**
 * Administrator editor of the Forge price table (TASK-0035 §5.3, §5.8).
 *
 * Three facts about the API shape this editor:
 *
 * - `PUT /admin/forge/pricing` **replaces** the table rather than patching it, so the panel edits the whole list in local
 *   state and submits it once. That is what keeps the panel's state and the server's state identical without a
 *   per-entry concurrency token;
 * - the **configured** table arrives beside the stored one and is rendered read-only, because it comes from deployment
 *   configuration: merging it into the editable list would make the next save copy it into the stored table;
 * - the **signup reward** is displayed but not editable, because the endpoint publishes it as context for the prices —
 *   it is a deployment decision, not a per-table one.
 *
 * The prices are integers per 1 000 tokens, so every input is a number field with a minimum of zero: a negative price
 * would credit an account on every call, and the server refuses it anyway.
 *
 * **The model identifier is no longer typed.** A row that names a profile prices *that profile's own model*, which the
 * profile already declares, so asking the operator to retype it only created a way to disagree with the profile. The
 * selector therefore offers only profiles that declare a model, and the derived model is shown read-only beside it. The
 * one row that keeps a model field is the **global** override: it applies to every connection at once and so has no
 * profile to take a model from — dropping the field there would leave the global row unpricable.
 */

import { Plus, Save, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { LtrInput } from '@/components/ui/input';
import { SelectControl } from '@/components/ui/react-select';
import type { SelectOption } from '@/components/ui/select-options';
import {
  Alert,
  Badge,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Spinner,
} from '@/components/ui/surface';
import { useForgePricing, useUpdateForgePricing } from '@/features/forge/hooks/useForge';
import { useProblemMessage } from '@/lib/errors';
import type { ForgePrice, SelectableProviderProfile } from '@/types/api';

/** One editable row, held as strings so a half-typed number is never coerced to `NaN` behind the user's back. */
interface PriceRow {
  /** Stable key for React; it follows the profile, which is what identifies a row now that the model is derived. */
  key: string;
  /**
   * Model the row prices. Derived from the selected profile for a profile row, and typed by the operator for the global
   * row, which has no profile to take a model from.
   */
  modelId: string;
  inputPerThousand: string;
  outputPerThousand: string;
  /** Profile the price belongs to, or `''` for the global override that applies to every connection (ADR-0019 §D5). */
  providerProfileId: string;
}

/** The model a row's profile declares, or `null` when the row is global or the profile is unknown. */
function modelOfProfile(
  providerProfileId: string,
  profiles: SelectableProviderProfile[],
): string | null {
  const profile = profiles.find((candidate) => candidate.id === providerProfileId);

  return profile?.modelId ?? null;
}

/** Turns the API rows into editable ones. */
function toRows(prices: ForgePrice[]): PriceRow[] {
  return prices.map((price) => ({
    key: `${price.providerProfileId ?? 'global'}:${price.modelId}`,
    modelId: price.modelId,
    inputPerThousand: String(price.inputPerThousand),
    outputPerThousand: String(price.outputPerThousand),
    providerProfileId: price.providerProfileId ?? '',
  }));
}

/** Renders the price table, the read-only configured prices and the save action. */
export function ForgePricingEditor() {
  const { t } = useTranslation();
  const messageFor = useProblemMessage();
  const pricing = useForgePricing();
  const update = useUpdateForgePricing();
  const [rows, setRows] = useState<PriceRow[]>([]);
  const [saved, setSaved] = useState(false);

  /** Every profile a row may reference, as the API published them. */
  const profiles = pricing.data?.providerProfiles ?? [];

  /**
   * The profiles a price row can actually be built on.
   *
   * A profile row takes its model from the profile, so a profile that declares no model has nothing to key a price on and
   * is left out of the selector rather than offered and then refused by the server. The global row is unaffected: it names
   * its own model.
   */
  const priceableProfiles = profiles.filter((profile) => (profile.modelId ?? '').trim().length > 0);

  useEffect(() => {
    if (pricing.data) {
      setRows(toRows(pricing.data.models));
    }
  }, [pricing.data]);

  /** Replaces one row's field without losing the others. */
  function patchRow(key: string, patch: Partial<Omit<PriceRow, 'key'>>): void {
    setRows((current) => current.map((row) => (row.key === key ? { ...row, ...patch } : row)));
    setSaved(false);
  }

  /**
   * Appends a row.
   *
   * It starts on the first profile that declares a model, because a profile row no longer carries a model the operator
   * types — the profile supplies it. When no profile is priceable the row starts as the global override, which is the one
   * kind of row whose model is still entered by hand.
   */
  function addRow(): void {
    const priceable = priceableProfiles[0];

    setRows((current) => [
      ...current,
      {
        key: `new-${current.length}-${Date.now()}`,
        modelId: priceable?.modelId ?? '',
        inputPerThousand: '0',
        outputPerThousand: '0',
        providerProfileId: priceable?.id ?? '',
      },
    ]);
    setSaved(false);
  }

  /**
   * Selects a row's profile and takes that profile's model with it.
   *
   * The model is not a separate field for a profile row, so it is written here rather than typed: choosing a profile is
   * what decides which model the row prices. Selecting the global option clears the model, because the operator must then
   * name it.
   */
  function selectProfile(key: string, providerProfileId: string): void {
    patchRow(key, {
      providerProfileId,
      modelId: modelOfProfile(providerProfileId, profiles) ?? '',
    });
  }

  /** Drops a row; saving without it is how an override is removed. */
  function removeRow(key: string): void {
    setRows((current) => current.filter((row) => row.key !== key));
    setSaved(false);
  }

  /** Submits the whole table, with the numbers parsed back from the fields. */
  function save(): void {
    setSaved(false);
    update.mutate(
      {
        models: rows
          .filter((row) => row.modelId.trim().length > 0)
          .map((row) => ({
            modelId: row.modelId.trim(),
            inputPerThousand: Number.parseInt(row.inputPerThousand, 10) || 0,
            outputPerThousand: Number.parseInt(row.outputPerThousand, 10) || 0,
            // An empty selection is the global override, which is what the column means by `null` on the wire.
            providerProfileId: row.providerProfileId === '' ? null : row.providerProfileId,
          })),
      },
      { onSuccess: () => setSaved(true) },
    );
  }

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between gap-2">
        <div className="flex flex-col gap-1">
          <CardTitle>{t('forge.pricingTitle')}</CardTitle>
          <CardDescription>{t('forge.pricingDescription')}</CardDescription>
        </div>

        <Button type="button" variant="outline" size="sm" onClick={addRow}>
          <Plus aria-hidden className="h-4 w-4" />
          {t('forge.addModel')}
        </Button>
      </CardHeader>

      <CardContent className="flex flex-col gap-4">
        {pricing.isPending ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Spinner />
            {t('common.loading')}
          </div>
        ) : null}

        {pricing.isError ? <Alert variant="destructive">{messageFor(pricing.error)}</Alert> : null}

        {update.isError ? <Alert variant="destructive">{messageFor(update.error)}</Alert> : null}

        {saved ? <Alert variant="success">{t('forge.pricingSaved')}</Alert> : null}

        {pricing.isSuccess ? (
          <>
            <dl className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
              <div className="flex items-center gap-2">
                <dt className="text-xs text-muted-foreground">{t('forge.signupReward')}</dt>
                <dd>
                  <Badge variant="outline">{pricing.data.signupReward}</Badge>
                </dd>
              </div>

              <div className="flex items-center gap-2">
                <dt className="text-xs text-muted-foreground">{t('forge.defaultPrice')}</dt>
                <dd className="text-xs">
                  {t('forge.pricePair', {
                    input: pricing.data.defaultInputPerThousand,
                    output: pricing.data.defaultOutputPerThousand,
                  })}
                </dd>
              </div>
            </dl>

            <PriceTable
              rows={rows}
              profiles={priceableProfiles}
              onChange={patchRow}
              onRemove={removeRow}
              onSelectProfile={selectProfile}
            />
          </>
        ) : null}

        <div>
          <Button type="button" onClick={save} disabled={update.isPending || !pricing.isSuccess}>
            {update.isPending ? <Spinner /> : <Save aria-hidden className="h-4 w-4" />}
            {update.isPending ? t('common.saving') : t('forge.savePricing')}
          </Button>
        </div>

        {pricing.isSuccess && pricing.data.configuredModels.length > 0 ? (
          <div className="flex flex-col gap-2 rounded-md border border-border p-3">
            <h4 className="text-sm font-medium">{t('forge.configuredTitle')}</h4>
            <p className="text-xs text-muted-foreground">{t('forge.configuredHint')}</p>

            <ul className="flex flex-col gap-1 text-xs">
              {pricing.data.configuredModels.map((price) => (
                <li key={price.modelId} className="flex items-center justify-between gap-2">
                  <span dir="ltr" className="numeric-token">
                    {price.modelId}
                  </span>
                  <span dir="ltr" className="numeric-token">
                    {t('forge.pricePair', {
                      input: price.inputPerThousand,
                      output: price.outputPerThousand,
                    })}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

/** Props accepted by {@link PriceTable}. */
interface PriceTableProps {
  /** Editable rows. */
  rows: PriceRow[];
  /**
   * Profiles a row may reference: the ones that declare a model, so the column can offer a selector rather than an
   * identifier (ADR-0019 §5) and the model never has to be typed for a profile row.
   */
  profiles: SelectableProviderProfile[];
  /** Applies a field change to one row. */
  onChange: (key: string, patch: Partial<Omit<PriceRow, 'key'>>) => void;
  /** Removes one row. */
  onRemove: (key: string) => void;
  /** Selects a row's profile, which also writes the model that profile declares onto the row. */
  onSelectProfile: (key: string, providerProfileId: string) => void;
}

/** The editable table of overrides. */
function PriceTable({ rows, profiles, onChange, onRemove, onSelectProfile }: PriceTableProps) {
  const { t } = useTranslation();

  /**
   * The profile column's options, rebuilt only when the profile list or the language changes.
   *
   * The empty row is the global override, so its *value* is the empty identifier: a row without a profile applies to every
   * connection (ADR-0019 §D5). Only profiles that declare a model are listed, because that model is what the row prices.
   */
  const profileOptions: SelectOption[] = useMemo(
    () => [
      { value: '', label: t('forge.profileGlobal') },
      ...profiles.map((profile) => ({ value: profile.id, label: profile.displayName })),
    ],
    [profiles, t],
  );

  if (rows.length === 0) {
    return <p className="text-sm text-muted-foreground">{t('forge.pricingEmpty')}</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border text-xs text-muted-foreground">
            <th scope="col" className="p-2 text-start font-medium">
              {t('forge.columnProviderProfile')}
            </th>
            <th scope="col" className="p-2 text-start font-medium">
              {t('forge.columnModelId')}
            </th>
            <th scope="col" className="p-2 text-start font-medium">
              {t('forge.columnInputPrice')}
            </th>
            <th scope="col" className="p-2 text-start font-medium">
              {t('forge.columnOutputPrice')}
            </th>
            <th scope="col" className="p-2 text-start font-medium">
              <span className="sr-only">{t('common.remove')}</span>
            </th>
          </tr>
        </thead>

        <tbody>
          {rows.map((row) => (
            <tr key={row.key} className="border-b border-border last:border-0">
              <td className="p-2">
                {/* The empty option is the global override: a row without a profile applies to every connection. Only
                    profiles that declare a model are listed, because that model is what the row prices. The column header
                    is the field's only name here, so it is handed over as the control's accessible name. */}
                <SelectControl
                  inputId={`forge-price-profile-${row.key}`}
                  aria-label={t('forge.columnProviderProfile')}
                  options={profileOptions}
                  value={row.providerProfileId}
                  onChange={(chosen) => onSelectProfile(row.key, chosen ?? '')}
                />
              </td>

              <td className="p-2">
                {row.providerProfileId === '' ? (
                  /* The global override has no profile to take a model from, so it is the one row that still names its
                     own model. A model identifier is always Latin, so the field opts into `dir="ltr"`. */
                  <LtrInput
                    aria-label={t('forge.columnModelId')}
                    value={row.modelId}
                    placeholder="gpt-4o-mini"
                    onChange={(event) => onChange(row.key, { modelId: event.target.value })}
                  />
                ) : (
                  /* Derived, not typed: the row prices the model its profile declares, and showing it read-only is what
                     keeps the table honest about which model is being priced. */
                  <span
                    aria-label={t('forge.columnModelId')}
                    dir="ltr"
                    className="numeric-token inline-block px-2 py-1 text-xs text-muted-foreground"
                  >
                    {row.modelId === '' ? t('forge.modelUnset') : row.modelId}
                  </span>
                )}
              </td>

              <td className="p-2">
                <LtrInput
                  type="number"
                  min={0}
                  aria-label={t('forge.columnInputPrice')}
                  value={row.inputPerThousand}
                  onChange={(event) => onChange(row.key, { inputPerThousand: event.target.value })}
                />
              </td>

              <td className="p-2">
                <LtrInput
                  type="number"
                  min={0}
                  aria-label={t('forge.columnOutputPrice')}
                  value={row.outputPerThousand}
                  onChange={(event) => onChange(row.key, { outputPerThousand: event.target.value })}
                />
              </td>

              <td className="p-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  aria-label={t('common.remove')}
                  onClick={() => onRemove(row.key)}
                >
                  <Trash2 aria-hidden className="h-4 w-4" />
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
